import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import styles from "./AssemblyVoting.module.css";
import { QuorumType } from "src/stores/assemblies/VotingStore";

interface IAssemblyVotingBase {
    assemblyid?: string;
}

const AssemblyVotingTitle = observer(function AssemblyVotingTitle() {
    const { votingStore } = useContext(RootStoreContext);
    return <div className={styles.VotingTitle}>{votingStore.selectedVoting.question}</div>;
});

const AssemblyVotingResultText = observer(function AssemblyVotingResultText() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);
    const notQuorateAndUnanimous = votingStore.votingBasis.quorum === QuorumType.UNANIMITY && !votingStore.isQuorate;

    if (!votingStore.votingResultText) {
        return null;
    }

    return (
        <div className={notQuorateAndUnanimous ? styles.ResultHeadernotQuorate : styles.ResultHeader}>
            <div
                className={
                    votingStore.isVotingResultTie || notQuorateAndUnanimous
                        ? styles.ResultHeaderIconWrapperTie
                        : styles.ResultHeaderIconWrapper
                }
                style={{ backgroundColor: votingStore.votingColorResult }}
            >
                {votingStore.isVotingResultTie || notQuorateAndUnanimous ? (
                    <UI.Icon icon={UI.SVGIcon.Close} size={16} color="white" />
                ) : (
                    <UI.Icon icon={UI.SVGIcon.Check} size={16} color="white" />
                )}
            </div>
            <div className={styles.ResultHeaderContent}>
                <div
                    className={
                        votingStore.isVotingResultTie || notQuorateAndUnanimous
                            ? styles.ResultHeaderTextTie
                            : styles.ResultHeaderText
                    }
                    style={{ color: votingStore.votingColorResult }}
                >
                    {t("screens.assemblies.vote.voting_result")}: {votingStore.votingResultText}
                </div>
                {notQuorateAndUnanimous ? (
                    // 1) unanimity
                    <div className={styles.ResultHeaderSubtitle}>
                        {t("screens.assemblies.vote.all_tenant_required")}
                    </div>
                ) : votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY ? (
                    [
                        { key: votingStore.votingBasis.key1, mls: votingStore.key1Mls },
                        { key: votingStore.votingBasis.key2!, mls: votingStore.key2Mls }
                    ].map(({ key, mls }, i) => (
                        <div
                            key={i}
                            className={styles.ResultHeaderSubtitle}
                            dangerouslySetInnerHTML={{
                                __html: votingStore.getQualifiedMajorityHtmlForKey(key, mls)
                            }}
                        />
                    ))
                ) : (
                    // fallback: only key1
                    <div
                        className={styles.ResultHeaderSubtitle}
                        dangerouslySetInnerHTML={{
                            __html: votingStore.getQualifiedMajorityHtmlForKey(
                                votingStore.votingBasis.key1,
                                votingStore.key1Mls
                            )
                        }}
                    />
                )}
            </div>
        </div>
    );
});

const AssemblyVotingPieChart = observer(function AssemblyVotingPieChart() {
    const { votingStore } = useContext(RootStoreContext);
    const isSimpleMajorityOrUnamity =
        votingStore.votingBasis.quorum === QuorumType.SIMPLE_MAJORITY ||
        votingStore.votingBasis.quorum === QuorumType.UNANIMITY;
    const isQualifiedMajority = votingStore.votingBasis.quorum === QuorumType.QUALIFIED_MAJORITY;

    if (isSimpleMajorityOrUnamity) {
        return (
            <>
                {votingStore.pieChartList.some((item) => item.number > 0) && (
                    <div className={styles.VotingInforAndPieChart}>
                        <div className={styles.PieChart}>
                            <UI.PieChart pieChartList={votingStore.pieChartList} />
                        </div>
                    </div>
                )}
            </>
        );
    } else if (isQualifiedMajority) {
        return (
            <>
                {votingStore.pieChartList.some((item) => item.number > 0) && (
                    <div className={styles.VotingInforAndPieChart}>
                        <div className={styles.PieChart}>
                            <UI.DoublePieChart
                                innerList={votingStore.pieChartList}
                                outerList={votingStore.pieChartOuterList}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    return null;
});

const AssemblyVotingStatsRow = observer(function AssemblyVotingStatsRow() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);

    return (
        <div className={styles.ResultStats}>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>{votingStore.quorumMls}</div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.quorum")}</div>
            </div>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>
                    {votingStore.key1Mls}
                    {votingStore.key2Mls.length > 0 && ` / ${votingStore.key2Mls}`}
                </div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.voting_method")}</div>
            </div>
            <div className={styles.StatItem}>
                <div className={styles.StatTitle}>{votingStore.isQuorate ? t("labels.yes") : t("labels.no")}</div>
                <div className={styles.StatLabel}>{t("screens.assemblies.vote.quorate")}</div>
            </div>
        </div>
    );
});

const AssemblyVotingVoting = observer(function AssemblyVotingVoting() {
    const { votingStore } = useContext(RootStoreContext);
    const {
        votingTenants,
        votingComponentResponse,
        selectedVoting,
        votingOptions,
        votingColors,
        setVotingComponentResponse
    } = votingStore;
    const initialDataVersion = votingComponentResponse.map((r) => r.selectedColumn ?? 0).join(",");

    const tenantRows = useMemo(() => {
        return votingTenants.map((tenant) => {
            const resp = votingComponentResponse.find((r) => r.id === tenant.tenantid);

            return {
                id: tenant.tenantid,
                label: tenant.name,
                selectedRow: resp?.selectedColumn ?? 0,
                active: true
            };
        });
    }, [votingTenants, votingComponentResponse]);

    const handleSelectionChange = useCallback(
        (data: { id: string; selectedColumn: number | null }[]) => {
            setVotingComponentResponse(data);
        },
        [setVotingComponentResponse]
    );

    return (
        <UI.Voting
            key={`${selectedVoting.votingid}-${initialDataVersion}`}
            numberOfColumn={votingOptions.length}
            headers={["WQ", ...votingOptions.map((o) => o.text)]}
            withMainRow
            rows={tenantRows}
            secondRowLabels={votingTenants.map((t) => t.unitvalue.toString())}
            buttonColors={votingColors}
            onSelectionChange={handleSelectionChange}
            withSearchInput
        />
    );
});

const AssemblyVotingSaveButton = observer(function AssemblyVotingSaveButton() {
    const { t } = useTranslation();
    const { votingStore } = useContext(RootStoreContext);
    const { saveVotingResult } = votingStore;

    const handleSave = useCallback(() => {
        saveVotingResult();
    }, [saveVotingResult]);

    return (
        <div className={styles.SaveButtonWrapper}>
            <UI.Button
                onClick={handleSave}
                className={styles.SaveButton}
                label={t("labels.save")}
                disabled={!votingStore.hasVotingResultChanged}
            />
        </div>
    );
});

export const AssemblyVoting = observer(function AssemblyVotingBase(props: IAssemblyVotingBase) {
    const { votingStore } = useContext(RootStoreContext);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (!votingStore.selectedVoting.votingid) {
            setIsLoaded(false);
            return;
        }

        // kick off loading
        setIsLoaded(false);
        votingStore.setVotingComponentResponse([]);

        // async load helper
        const loadAll = async () => {
            await votingStore.loadVotingTenantsAndOptions();
            await votingStore.loadVotingInformationBasis();
            await votingStore.loadVotingDetails(
                votingStore.selectedVoting.votingid,
                votingStore.selectedVoting.votingagendaitemid
            );
            setIsLoaded(true);
        };
        loadAll();
    }, [votingStore.selectedVoting.votingid, votingStore]);

    // after loadVotingTenantsAndOptions() runs and votingResults/votingTenants/votingOptions are fresh:
    useEffect(() => {
        const { votingTenants, votingOptions, votingResults } = votingStore;

        if (
            votingStore.selectedVoting.votingid &&
            votingTenants.length > 0 &&
            votingOptions.length > 0 &&
            votingResults.length > 0
        ) {
            const initialResponse = votingTenants.map((tenant) => {
                const result = votingResults.find((r) => r.tenantid === tenant.tenantid);
                if (!result?.votingoptionid) {
                    return { id: tenant.tenantid, selectedColumn: null };
                }
                const optIndex = votingOptions.findIndex((o) => o.votingoptionid === result.votingoptionid);
                return { id: tenant.tenantid, selectedColumn: optIndex >= 0 ? optIndex + 1 : null };
            });

            votingStore.setVotingComponentResponse(initialResponse);
        }
    }, [votingStore, votingStore.selectedVoting.votingid, votingStore.votingTenants, votingStore.votingOptions, votingStore.votingResults, votingStore.setVotingComponentResponse]);

    if (
        !isLoaded ||
        !votingStore.selectedVoting.votingid ||
        votingStore.votingTenants.length === 0 ||
        votingStore.votingOptions.length === 0 ||
        votingStore.votingResults.length === 0
    ) {
        return null;
    }

    return (
        <UI.Card>
            <AssemblyVotingTitle />
            <div className={styles.ResultInformations}>
                <AssemblyVotingResultText />
                <AssemblyVotingPieChart />
                <AssemblyVotingStatsRow />
            </div>
            <AssemblyVotingVoting />
            <AssemblyVotingSaveButton />
        </UI.Card>
    );
});
